import { useMemo } from 'react';
import { pricingData } from '@/data/pricing';

export interface PricingResult {
  subtotal: number;
  cleaningFee: number;
  total: number;
  nights: number;
  isSummer: boolean;
  requiresQuote: boolean; // Pour séjours > 7 nuits hors été
  error?: string;
}

export const usePricing = (startDate?: Date, endDate?: Date): PricingResult | null => {
  return useMemo(() => {
    if (!startDate || !endDate) {
      return null;
    }

    const nights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (nights < 1) {
      return {
        subtotal: 0,
        cleaningFee: pricingData.forfait_menage,
        total: pricingData.forfait_menage,
        nights,
        isSummer: false,
        requiresQuote: false,
        error: "La durée du séjour doit être d'au moins 1 nuit"
      };
    }

    // Vérifier si le séjour est en période estivale
    const summerPeriod = pricingData.tarification.find(
      (t): t is Extract<typeof t, { periode: { debut: string; fin: string } }> => 
        typeof t.periode === 'object' && 'debut' in t.periode
    );

    if (!summerPeriod) {
      return null;
    }

    const isSummer = isInSummerPeriod(startDate, endDate, summerPeriod.periode);

    if (isSummer) {
      // Période estivale : minimum 7 nuits, tarif à la nuit
      if (nights < summerPeriod.min_nuits) {
        return {
          subtotal: 0,
          cleaningFee: pricingData.forfait_menage,
          total: pricingData.forfait_menage,
          nights,
          isSummer: true,
          requiresQuote: false,
          error: `Minimum ${summerPeriod.min_nuits} nuits en période estivale`
        };
      }

      const subtotal = summerPeriod.tarif_nuit * nights;
      return {
        subtotal,
        cleaningFee: pricingData.forfait_menage,
        total: subtotal + pricingData.forfait_menage,
        nights,
        isSummer: true,
        requiresQuote: false
      };
    }

    // Hors période estivale
    const offSeasonPricing = pricingData.tarification.find(
      (t): t is Extract<typeof t, { periode: "hors_ete" }> => 
        typeof t.periode === 'string' && t.periode === 'hors_ete'
    );

    if (!offSeasonPricing) {
      return null;
    }

    if (nights < offSeasonPricing.min_nuits) {
      return {
        subtotal: 0,
        cleaningFee: pricingData.forfait_menage,
        total: pricingData.forfait_menage,
        nights,
        isSummer: false,
        requiresQuote: false,
        error: `Minimum ${offSeasonPricing.min_nuits} nuits hors période estivale`
      };
    }

    // Séjours de plus de 7 nuits hors été : devis requis
    if (nights > 7) {
      return {
        subtotal: 0,
        cleaningFee: pricingData.forfait_menage,
        total: 0,
        nights,
        isSummer: false,
        requiresQuote: true
      };
    }

    // Trouver le tarif dégressif correspondant
    const degressiveTarif = offSeasonPricing.tarifs_degressifs.find(t => t.nuits === nights);
    
    if (!degressiveTarif) {
      return {
        subtotal: 0,
        cleaningFee: pricingData.forfait_menage,
        total: pricingData.forfait_menage,
        nights,
        isSummer: false,
        requiresQuote: false,
        error: "Tarif non disponible pour cette durée"
      };
    }

    const subtotal = degressiveTarif.tarif_sejour;
    return {
      subtotal,
      cleaningFee: pricingData.forfait_menage,
      total: subtotal + pricingData.forfait_menage,
      nights,
      isSummer: false,
      requiresQuote: false
    };
  }, [startDate, endDate]);
};

// Fonction pour vérifier si les dates sont en période estivale
const isInSummerPeriod = (
  startDate: Date, 
  endDate: Date, 
  period: { debut: string; fin: string }
): boolean => {
  const year = startDate.getFullYear();
  
  // Créer les dates de début et fin de la période estivale pour l'année courante
  const [summerStartMonth, summerStartDay] = period.debut.split('-').map(Number);
  const [summerEndMonth, summerEndDay] = period.fin.split('-').map(Number);
  
  const summerStart = new Date(year, summerStartMonth - 1, summerStartDay);
  const summerEnd = new Date(year, summerEndMonth - 1, summerEndDay);
  
  // Vérifier si le séjour chevauche la période estivale
  return startDate <= summerEnd && endDate >= summerStart;
};
